import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, ShieldCheck, CreditCard, Landmark } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { SERVICES } from "@/data/services";

const COMPARISON = [
  { feature: "ATS-optimised UK CV (2-page standard)", ids: ["cv-writing", "ultimate-bundle"] },
  { feature: "High-impact cover letter", ids: ["cover-letter", "ultimate-bundle"] },
  { feature: "LinkedIn profile overhaul", ids: ["linkedin-optimization", "ultimate-bundle"] },
  { feature: "UK job search strategy guide", ids: ["job-search-assistance", "ultimate-bundle"] },
  { feature: "60-min mock interview (STAR method)", ids: ["interview-prep"] },
  { feature: "Priority email support", ids: ["ultimate-bundle"] },
  { feature: "2 revision rounds", ids: ["cv-writing", "ultimate-bundle"] },
];

const STEPS = [
  { n: "01", title: "Select & Checkout", text: "Choose your service or bundle and pay securely in GBP via Stripe — card or Apple Pay." },
  { n: "02", title: "Complete the Questionnaire", text: "Upload your existing CV and tell us your degree, university, visa status and target roles in your client portal." },
  { n: "03", title: "HR Drafting", text: "Your documents are written by UK HR professionals — never templated, never outsourced." },
  { n: "04", title: "Review & Deliver", text: "Receive your draft, request revisions through the portal, and get final Word + PDF files." },
];

export default function Services() {
  const navigate = useNavigate();
  const bundle = SERVICES.find((s) => s.id === "ultimate-bundle");
  const singles = SERVICES.filter((s) => s.id !== "ultimate-bundle");

  return (
    <div data-testid="services-page">
      {/* HEADER */}
      <section className="mx-auto max-w-7xl px-5 pb-16 pt-20 lg:px-8 lg:pt-28">
        <span className="overline-label">Services & Pricing</span>
        <h1 className="mt-5 max-w-3xl font-serif text-4xl font-semibold leading-tight text-slate-50 sm:text-5xl lg:text-6xl" data-testid="services-headline">
          <MaskedLine delay={0.1}>Invest in Your UK Career.</MaskedLine>
          <MaskedLine delay={0.3} className="italic text-[#C5A059]">Affordable, HR-Expert Solutions.</MaskedLine>
        </h1>
        <Reveal delay={0.5}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Built for international students and graduates entering the UK job market. Select standard individual services or unlock massive value with our premium bundle — all prices fixed in GBP (£), with Basic (10 working days) or Advanced (3–5 working days) delivery on every service.
          </p>
          <div className="mt-8 inline-flex flex-wrap items-center gap-x-6 gap-y-3 rounded-full border border-[#C5A059]/40 bg-[#111827] px-6 py-3" data-testid="gbp-banner">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#C5A059]"><CreditCard size={16} /> Pay in GBP (£)</span>
            <span className="text-xs text-slate-400">Secure Stripe checkout · Debit / Credit / Apple Pay</span>
          </div>
        </Reveal>
      </section>

      {/* BUNDLE HERO CARD */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <div className="card-surface gold-stroke relative overflow-hidden p-8 lg:p-12" data-testid="service-card-ultimate-bundle">
            <div className="absolute right-8 top-8 hidden rounded-full bg-[#C5A059] px-4 py-1.5 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-[#0A0F1D] sm:block">Best Value</div>
            <div className="grid gap-10 lg:grid-cols-2">
              <div>
                <span className="overline-label">Featured Bundle</span>
                <h2 className="mt-3 font-serif text-3xl font-semibold text-slate-50 sm:text-4xl">{bundle.name}</h2>
                <p className="mt-4 text-base leading-relaxed text-slate-300">{bundle.description}</p>
                <div className="mt-6 flex items-end gap-4">
                  <span className="font-mono text-5xl font-semibold text-[#C5A059]">£{bundle.price.toFixed(2)}</span>
                  <span className="pb-2 font-mono text-lg text-slate-500 line-through">£{bundle.originalValue}</span>
                  <span className="mb-2 rounded-full bg-emerald-500/15 px-3 py-1 font-mono text-xs font-semibold text-emerald-400">{bundle.savings}</span>
                </div>
                <button onClick={() => navigate("/order?package=ultimate-bundle")} className="btn-gold mt-8" data-testid="bundle-order-btn">
                  Get the Bundle <ArrowRight size={16} />
                </button>
              </div>
              <ul className="space-y-4 self-center">
                {bundle.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm leading-relaxed text-slate-200">
                    <Check size={17} className="mt-0.5 shrink-0 text-[#C5A059]" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </section>

      {/* INDIVIDUAL SERVICES */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <Reveal>
          <span className="overline-label">Individual Career Services</span>
          <h2 className="mt-4 font-serif text-3xl font-semibold text-slate-50 sm:text-4xl">Standalone expertise, à la carte</h2>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {singles.map((s, i) => (
            <Reveal key={s.id} delay={(i % 3) * 0.08}>
              <div className="card-surface group flex h-full flex-col p-8 transition-colors duration-300 hover:border-[#C5A059]/50" data-testid={`service-card-${s.id}`}>
                <div className="flex items-baseline justify-between">
                  <span className="font-mono text-4xl text-slate-50">£{s.price.toFixed(2)}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{s.turnaround}</span>
                </div>
                <h3 className="mt-4 font-serif text-2xl font-semibold text-slate-50">{s.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.description}</p>
                <ul className="mt-6 flex-1 space-y-3 border-t border-slate-700/60 pt-6">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-xs leading-relaxed text-slate-300">
                      <Check size={14} className="mt-0.5 shrink-0 text-[#C5A059]" /> {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => navigate(`/order?package=${s.id}`)} className="btn-ghost mt-8 w-full group-hover:border-[#C5A059] group-hover:text-[#C5A059]" data-testid={`service-order-btn-${s.id}`}>
                  Order for £{s.price.toFixed(2)}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="border-y border-slate-800/60 bg-[#0C1222]">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
          <Reveal>
            <span className="overline-label">Compare</span>
            <h2 className="mt-4 font-serif text-3xl font-semibold text-slate-50 sm:text-4xl">What's included, side by side</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="card-surface mt-12 overflow-x-auto" data-testid="comparison-table">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-700/60 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">
                    <th className="px-6 py-5">What's included</th>
                    <th className="px-6 py-5">Standalone</th>
                    <th className="px-6 py-5 text-[#C5A059]">Ultimate Bundle</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.map((row) => (
                    <tr key={row.feature} className="border-b border-slate-800/60 last:border-0">
                      <td className="px-6 py-4 text-slate-300">{row.feature}</td>
                      <td className="px-6 py-4">
                        {row.ids.some((id) => id !== "ultimate-bundle") ? (
                          <Check size={16} className="text-slate-400" />
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {row.ids.includes("ultimate-bundle") ? (
                          <Check size={16} className="text-[#C5A059]" />
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROCESS */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <Reveal>
          <span className="overline-label">The Process</span>
          <h2 className="mt-4 font-serif text-3xl font-semibold text-slate-50 sm:text-4xl">From checkout to final draft in four steps</h2>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="relative h-full rounded-2xl border border-slate-700/50 p-8" data-testid={`process-step-${i}`}>
                <span className="font-serif text-5xl font-semibold text-[#C5A059]/25">{s.n}</span>
                <h3 className="mt-4 font-serif text-xl font-semibold text-slate-50">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-14 flex flex-wrap items-center justify-between gap-6 rounded-2xl border border-slate-700/50 bg-[#111827] p-8" data-testid="payment-trust-banner">
            <div className="flex items-center gap-4">
              <Landmark size={28} className="text-[#C5A059]" />
              <div>
                <div className="font-semibold text-slate-50">Transparent GBP billing</div>
                <div className="text-sm text-slate-400">Fixed prices, no hidden fees, secure Stripe processing worldwide.</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <ShieldCheck size={20} className="text-emerald-400" />
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-slate-400">SSL Secured · Stripe Verified</span>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
